import React, { useEffect, useMemo, useState } from 'react'
import { Participant as TwilioParticipant } from 'twilio-video'

import { css, styled } from 'design'

import { Participant } from '../participant'
import { LocalTrackData } from '../tracks/use_local_tracks'

export type GalleryProps = {
  remoteParticipants: TwilioParticipant[]
  localParticipant: TwilioParticipant
  localTrackData: LocalTrackData
  callRecord: any
} & React.HTMLAttributes<HTMLDivElement>

export const Gallery = ({
  remoteParticipants,
  localParticipant,
  localTrackData,
  callRecord,
}: GalleryProps) => {
  const [pinnedParticipantId, setPinnedParticipantId] = useState<
    string | undefined
  >()
  const showLocalParticipant =
    !!localTrackData &&
    (localTrackData.isVideoEnabled || localTrackData.isScreenShareEnabled)

  const multipleVideos =
    remoteParticipants.length + (showLocalParticipant ? 1 : 0) > 1

  useEffect(() => {
    if (!multipleVideos) setPinnedParticipantId(undefined)
  }, [multipleVideos])

  const gridStyle: any = useMemo(() => {
    const count = remoteParticipants.length + (showLocalParticipant ? 1 : 0)
    if (pinnedParticipantId) {
      return getPinnedLayoutStyle(count)
    } else {
      return getGridLayoutStyle(count)
    }
  }, [remoteParticipants.length, pinnedParticipantId, showLocalParticipant])

  const selectedParticipant = useMemo(()=> {
    return pinnedParticipantId &&
    [...remoteParticipants, localParticipant].find(
      (p) => p?.identity === pinnedParticipantId,
    )
  }, [localParticipant, remoteParticipants, pinnedParticipantId])

  const { identity: localIdentity } = localParticipant || {}
  const localIsPinned =
    !!pinnedParticipantId && pinnedParticipantId === localIdentity


  return (
    <section className={sectionStyles()}>
      {selectedParticipant && (
        <Participant
          participant={selectedParticipant}
          callRecord={callRecord}
          localTrackData={
            selectedParticipant.identity === localParticipant.identity
              ? localTrackData
              : undefined
          }
          setPinnedParticipantId={setPinnedParticipantId}
          isPinned={true}
          className={pinnedParticipantStyles()}
          multipleVideos={multipleVideos}
        />
      )}
      <GalleryContainer
        style={{
          ...gridStyle,
          ...(pinnedParticipantId ? { height: '20%' } : {}),
        }}
      >
        {remoteParticipants.reduce((acc: any, participant: any) => {
          const identity = participant.identity
          const pinned = pinnedParticipantId === identity

          if (!pinned) {
            acc.push(
              <Participant
                key={identity}
                participant={participant}
                callRecord={callRecord}
                setPinnedParticipantId={setPinnedParticipantId}
                isPinned={pinned}
                className={participantStyles({ selected: pinned })}
                multipleVideos={multipleVideos}
              />,
            )
          }
          return acc
        }, [])}
        {showLocalParticipant &&
          (!pinnedParticipantId ||
            pinnedParticipantId !== localParticipant?.identity)  && (
            <Participant
              participant={localParticipant}
              callRecord={callRecord}
              label="you"
              localTrackData={localTrackData}
              setPinnedParticipantId={setPinnedParticipantId}
              isPinned={localIsPinned}
              className={participantStyles({
                selected: localIsPinned,
              })}
              multipleVideos={multipleVideos}
            />
          )}
      </GalleryContainer>
    </section>
  )
}

const getPinnedLayoutStyle = (count: number) => {
  const columns = count > 10 ? 10 : count
  return { '--columns': columns, '--rows': 1 }
}

const MAXIMUM_REMOTE_PARTICIPANTS = 48
const MAXIMUM_DIMENSIONS = 7

export const getGridLayoutStyle = (count: number) => {
  // Currently the maximum allowed is a 7x7 grid (49), this can be adjusted
  if (count > MAXIMUM_REMOTE_PARTICIPANTS) {
    return { '--columns': MAXIMUM_DIMENSIONS, '--rows': MAXIMUM_DIMENSIONS }
  }
  if (count === 0) {
    return { '--columns': 1, '--rows': 1 }
  }

  const nearestSquareWidth = Math.ceil(Math.sqrt(count))
  const cellsCount = Math.pow(nearestSquareWidth, 2)
  const blankCells = cellsCount - count
  if (blankCells < nearestSquareWidth) {
    return { '--columns': nearestSquareWidth, '--rows': nearestSquareWidth }
  } else {
    return {
      '--columns': nearestSquareWidth - 1,
      '--rows': nearestSquareWidth,
    }
  }
}

const GalleryContainer = styled('section', {
  display: 'grid',
  gridGap: '2px',
  height: '100%',
  width: '100%',
  gridAutoFlow: 'column',
  gridAutoColumns: 'minmax(185px, 1fr)',
  overflowX: 'scroll',
  gridTemplateColumns: 'repeat(var(--columns), minmax(185px, 1fr))',
  gridTemplateRows: 'repeat(var(--rows), 1fr)',

  padding: '$3',

  '@md': {
    padding: 0,
  },
})

const sectionStyles = css({
  height: '100%',
})

const pinnedParticipantStyles = css({
  height: '80%',
})

const participantStyles = css({
  '@md': {
    border: '3px solid transparent',
    borderRadius: '$3',
  },

  variants: {
    selected: {
      true: {
        '@md': {
          borderColor: '$accent600',
        },
      },
    },
  },
})
